import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PhotoCamera,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface CameraModalProps {
  open: boolean;
  onClose: () => void;
  onPhotoTaken: (file: File) => void;
}

const CameraModal: React.FC<CameraModalProps> = ({ open, onClose, onPhotoTaken }) => {
  const { t } = useTranslation();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    let mounted = true;

    if (open) {
      setLoading(true);
      setError(null);

      const startCamera = async () => {
        try {
          console.log('Starting camera...');

          // Check if mediaDevices is available
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('Camera API not available');
          }

          let mediaStream: MediaStream;

          // Try environment (back) camera first
          try {
            console.log('Trying environment camera...');
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: { ideal: "environment" },
                width: { ideal: 1920 },
                height: { ideal: 1080 }
              }
            });
          } catch (err) {
            // Fallback to any available camera
            console.log('Fallback to any camera...', err);
            mediaStream = await navigator.mediaDevices.getUserMedia({
              video: {
                width: { ideal: 1920 },
                height: { ideal: 1080 }
              }
            });
          }

          if (!mounted) {
            // Component unmounted, stop tracks
            mediaStream.getTracks().forEach(track => track.stop());
            return;
          }

          console.log('Camera stream obtained:', mediaStream.active);
          setStream(mediaStream);

          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;

            // Wait for video to be ready
            videoRef.current.onloadedmetadata = () => {
              console.log('Video metadata loaded');
              if (mounted && videoRef.current) {
                videoRef.current.play()
                  .then(() => {
                    console.log('Video playing');
                    if (mounted) {
                      setLoading(false);
                    }
                  })
                  .catch((playErr) => {
                    console.error('Error playing video:', playErr);
                    if (mounted) {
                      setError('Erreur lors du démarrage de la vidéo');
                      setLoading(false);
                    }
                  });
              }
            };

            // Fallback timeout
            setTimeout(() => {
              if (mounted && loading) {
                console.log('Timeout reached, forcing loading to false');
                setLoading(false);
              }
            }, 5000);
          } else {
            setLoading(false);
          }

        } catch (err: any) {
          console.error('Error accessing camera:', err);
          if (mounted) {
            let errorMessage = 'Erreur d\'accès à la caméra';
            if (err.name === 'NotAllowedError') {
              errorMessage = 'Permission caméra refusée. Veuillez autoriser l\'accès à la caméra.';
            } else if (err.name === 'NotFoundError') {
              errorMessage = 'Aucune caméra trouvée sur cet appareil.';
            } else if (err.name === 'NotReadableError') {
              errorMessage = 'La caméra est déjà utilisée par une autre application.';
            }
            setError(errorMessage);
            setLoading(false);
          }
        }
      };

      startCamera();
    } else {
      // Reset states when closing
      setLoading(true);
      setError(null);
    }

    return () => {
      mounted = false;
      if (stream) {
        console.log('Stopping camera stream...');
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [open]);

  const handleCapture = () => {
    if (videoRef.current) {
      try {
        // Créer un canvas avec une taille optimisée
        const maxWidth = 1600; // Correspond à la limite dans useMaintenanceAI
        const maxHeight = 1600;
        const videoWidth = videoRef.current.videoWidth;
        const videoHeight = videoRef.current.videoHeight;
        
        // Calculer les dimensions tout en conservant le ratio
        let width = videoWidth;
        let height = videoHeight;
        if (width > height && width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }

        // S'assurer que les dimensions sont des entiers
        width = Math.floor(width);
        height = Math.floor(height);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        // Optimiser la qualité de l'image
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Nettoyer le canvas avant de dessiner
        ctx.clearRect(0, 0, width, height);
        
        // Dessiner l'image avec les nouvelles dimensions
        ctx.drawImage(videoRef.current, 0, 0, width, height);
        
        // Convertir en JPEG avec une qualité optimisée
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Vérifier la taille du blob
              if (blob.size > 5 * 1024 * 1024) { // Si plus de 5MB
                console.warn('Image size too large, reducing quality');
                // Réessayer avec une qualité réduite
                canvas.toBlob(
                  (reducedBlob) => {
                    if (reducedBlob) {
                      const file = new File([reducedBlob], 'receipt-photo.jpg', { type: 'image/jpeg' });
                      onPhotoTaken(file);
                      onClose();
                    }
                  },
                  'image/jpeg',
                  0.7
                );
              } else {
                const file = new File([blob], 'receipt-photo.jpg', { type: 'image/jpeg' });
                onPhotoTaken(file);
                onClose();
              }
            }
          },
          'image/jpeg',
          0.9
        );
      } catch (error) {
        console.error('Error capturing photo:', error);
        setError(t('vehicle.mileage.errors.captureError'));
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t('vehicle.mileage.takePhoto')}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Vérifiez que :
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Vous avez autorisé l'accès à la caméra
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Aucune autre application n'utilise la caméra
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Votre navigateur supporte l'API MediaDevices
              </Typography>
            </Box>
          </Alert>
        ) : (
          <Box sx={{ position: 'relative', minHeight: '300px', bgcolor: '#000' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  zIndex: 1
                }}
              >
                <CircularProgress sx={{ color: 'white' }} />
                <Typography color="white">
                  {t('vehicle.mileage.loadingCamera', 'Chargement de la caméra...')}
                </Typography>
              </Box>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                minHeight: '300px',
                maxHeight: '60vh',
                objectFit: 'contain',
                display: 'block',
                backgroundColor: '#000'
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCapture}
          startIcon={<PhotoCamera />}
          disabled={loading || !!error}
        >
          {t('vehicle.mileage.capture')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CameraModal;
